import { PathwaySettingsFormStore } from "@/admin/pathways/settings/PathwaySettingsForm"
import AddEntityTagButton from "@/admin/settings/tabs/AddEntityTagButton"
import EntityTag from "@/admin/settings/tabs/EntityTag"
import { toggleTag } from "@/admin/settings/tabs/entityTagUtils"
import { useLabel } from "@/core/context/LabelsContext"
import { ExperienceSettingsFormStore } from "@/pricing/PricingFormFields"
import { ProductEntityTagInput } from "@/product/settings/ExperienceSettingsForm"
import ExperienceSettingsLabel from "@/product/settings/ExperienceSettingsLabel"
import { useAdminProductLabel } from "@/product/util/hook/useProductLabel"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoButton, DiscoFormControl, DiscoIcon, DiscoIconButton } from "@disco-ui"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { TestIDProps } from "@utils/typeUtils"
import { toJS } from "mobx"
import { observer } from "mobx-react-lite"

interface Props extends TestIDProps {
  form: ExperienceSettingsFormStore | PathwaySettingsFormStore
  kind: "experience" | "pathway"
}

function ProductSettingsTags({ testid = "ProductSettingsTags", form, kind }: Props) {
  const { tagging } = useFeatureFlags()
  const experienceLabel = useLabel("admin_experience")
  const pathwayLabel = useAdminProductLabel("pathway")
  const productLabel = kind === "pathway" ? pathwayLabel : experienceLabel
  const classes = useStyles()

  if (!tagging) return null

  return (
    <DiscoFormControl
      label={<ExperienceSettingsLabel title={"Tags"} sectionId={"tags"} />}
      sublabel={`Attach tags to this ${productLabel.singular} for easy filtering and discoverability.`}
      testid={`ProductSettingsTags.Tags`}
      error={Boolean(form.errorsByField.entityTags)}
      errorMessages={form.errorsByField.entityTags}
    >
      <div data-testid={`${testid}.list`} className={classes.tagsList}>
        {form.state.entityTags.map((tag) => {
          const nameErrors = form.errorsByField[`addEntityTags.${tag.id}.name`] || []
          const entityTagIdErrors =
            form.errorsByField[`addEntityTags.${tag.id}.entityTagId`] || []

          return (
            <EntityTag
              key={tag.id}
              testid={`${testid}.list`}
              name={tag.name}
              onRemove={() => handleRemoveTag(toJS(tag))}
              errors={[...nameErrors, ...entityTagIdErrors]}
            />
          )
        })}

        <AddEntityTagButton
          testid={"ProductSettingsTags"}
          form={form}
          hideAddButton
          skipCreation
        >
          {(buttonProps) => {
            if (form.state.entityTags.length > 0) {
              return (
                <DiscoIconButton
                  size={"small"}
                  variant={"outlined"}
                  tooltip={"Add Tags"}
                  height={32}
                  width={32}
                  className={classes.addTagsIcon}
                  {...buttonProps}
                >
                  <DiscoIcon icon={"add"} />
                </DiscoIconButton>
              )
            }

            return (
              <DiscoButton
                variant={"outlined"}
                color={"grey"}
                leftIcon={"add"}
                {...buttonProps}
              >
                {"Add Tags"}
              </DiscoButton>
            )
          }}
        </AddEntityTagButton>
      </div>
    </DiscoFormControl>
  )

  function handleRemoveTag(tag: ProductEntityTagInput) {
    toggleTag(form, tag)
  }
}

const useStyles = makeUseStyles((theme) => ({
  addTagsIcon: {
    "& svg": {
      width: 16,
      height: 16,
    },
  },
  tagsList: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(0.5),
  },
}))

export default observer(ProductSettingsTags)
